import React from 'react';

import * as Style from './FilterPillGroup.module.scss';

export interface FilterPillOption {
    value: string;
    label: string;
    // Explicit active state — overrides the `selectedValues` lookup. Useful when
    // several pills share a value (e.g. the date presets) or when selection is
    // derived rather than a simple membership test.
    active?: boolean;
    // Non-interactive pill (e.g. a mutually-exclusive option that can't be picked
    // given the current selection).
    disabled?: boolean;
    // Stable React key for when `value` isn't unique across the options.
    key?: string;
    // Custom fill for the active state (e.g. a pastel status colour) instead of
    // the default brand primary. `activeTextColor` overrides the label colour.
    activeColor?: string;
    activeTextColor?: string;
    // Optional node before the label (e.g. a care-manager avatar).
    leading?: React.ReactNode;
}

interface FilterPillGroupProps {
    options: FilterPillOption[];
    // Values considered active; ignored for any option that sets `active`.
    selectedValues?: string[];
    onToggle: (value: string) => void;
}

// A wrap of toggleable pills. Works for single- and multi-select alike — the
// caller's `onToggle` decides the semantics, and multiple pills stay active
// together for multi-select filters.
const FilterPillGroup: React.FC<FilterPillGroupProps> = ({
    options,
    selectedValues = [],
    onToggle
}) => (
    <div className={Style.pills}>
        {options.map((option) => {
            const isActive = option.active ?? selectedValues.includes(option.value);
            // Custom-coloured active pills paint their fill inline; otherwise the
            // default primary `.pillActive` fill applies.
            const colored = isActive && !!option.activeColor;
            const className = [
                Style.pill,
                option.leading && Style.pillWithLeading,
                isActive && !option.activeColor && Style.pillActive,
                colored && Style.pillColored,
                option.disabled && Style.pillDisabled
            ]
                .filter(Boolean)
                .join(' ');

            return (
                <button
                    type="button"
                    key={option.key ?? option.value}
                    aria-pressed={isActive}
                    disabled={option.disabled}
                    className={className}
                    style={
                        colored
                            ? {
                                  backgroundColor: option.activeColor,
                                  borderColor: option.activeColor,
                                  color: option.activeTextColor
                              }
                            : undefined
                    }
                    onClick={() => onToggle(option.value)}
                >
                    {option.leading}
                    <span className={Style.label} data-text={option.label}>
                        {option.label}
                    </span>
                </button>
            );
        })}
    </div>
);

export default FilterPillGroup;
